import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import apiServices from '../../services/service';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateFormatter, DateTimeFormatter, NameFormatter } from '../../shared/controls/formatters';
import Loader from '../../shared/component/Loader';
import CustomStatusBar from '../sharedChild/grid/customStatusBar';
import {
  getGridDataByModuleKey,
  getMassEmailByModuleKey,
  getMassEmailCountByModuleKey,
} from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { contactInputData, filterParams } from '../../shared/constants';
import FilterHandler from '../sharedChild/grid/filterHandler';
import { columnTypes } from '../../shared/initials';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';

const MassEmailPage = ({ selectedOptions, handleClose, handleChange }) => {
  const themeValue = localStorage.getItem('selectedGridTheme');
  const [selectedTheme, setSelectedTheme] = useState(themeValue ? themeValue : 'balham');
  const location = useLocation();
  const stateData = useSelector((state) => state);
  const isLeadPath = location.pathname.includes('leads');

  const keySent = `massEmail${isLeadPath ? 'Lead' : 'Contact'}Sent`;
  const keySchedule = `massEmail${isLeadPath ? 'Lead' : 'Contact'}Schedule`;

  let storeLoading = isLoadingByKey(stateData, keySent);
  const [rowDataSent, setRowDataSent] = useState([]);
  const [rowDataScheduled, setRowDataScheduled] = useState([]);
  const [rowDataDraft, setRowDataDraft] = useState([]);
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const [loading, setLoading] = useState(storeLoading);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useFetchIfNeeded(getMassEmailByModuleKey, fetchDataAndUpdateStore, {
    key: 'massEmailLeadSent',
    inputData: {
      ...contactInputData,
      module: 'Lead',
      columnName: 'createdDateTime',
      status: '',
    },
  });

  useFetchIfNeeded(getMassEmailByModuleKey, fetchDataAndUpdateStore, {
    key: 'massEmailContactSent',
    inputData: {
      ...contactInputData,
      module: 'Contact',
      columnName: 'createdDateTime',
      status: '',
    },
  });

  const [activeTab, setActiveTab] = useState('Sent');
  const totalEmailSent = useSelector(getMassEmailByModuleKey(keySent));
  const totalEmailSchedule = useSelector(getMassEmailByModuleKey(keySchedule));
  const gridData = useSelector(
    getGridDataByModuleKey(activeTab === 'Sent' ? keySent : keySchedule)
  );

  const totalCountSent = useSelector(getMassEmailCountByModuleKey(keySent));
  const totalCountSchedule = useSelector(getMassEmailCountByModuleKey(keySchedule));

  const tabs = ['Sent', 'Scheduled'];

  useEffect(() => {
    if (activeTab === 'Scheduled' && !totalEmailSchedule) {
      const isLeadPath = location.pathname.includes('lead');
      const moduleType = isLeadPath ? 'Lead' : 'Contact';
      const key = isLeadPath ? 'massEmailLeadSchedule' : 'massEmailContactSchedule';

      dispatch(
        fetchDataAndUpdateStore({
          key,
          inputData: {
            ...contactInputData,
            module: moduleType,
            columnName: 'createdDateTime',
            status: 'Scheduled',
          },
        })
      );
    }
  }, [activeTab, dispatch, totalEmailSchedule]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressPivotMode: true, // 👈 Hide Pivot Mode toggle
            suppressRowGroups: false, // ✅ Allow row grouping
            suppressValues: false, // ✅ Allow aggregation if needed
            suppressPivots: true, // 👈 Hide the pivot section for columns
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
      position: 'left',
      defaultToolPanel: 'none', //show off filter tab bydefault.
    };
  }, []);
  const gridRef = useRef();
  const baseColumns = [
    {
      headerName: 'To',
      field: 'toAddress',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Email Template Subject',
      field: 'subject',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Date Scheduled',
      field: 'scheduledTime',
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: DateTimeFormatter,
    },
    {
      headerName: 'Last Modified By',
      field: 'createdBy',
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: NameFormatter,
    },
  ];

  const leadColumns = [
    {
      headerName: 'Company Name',
      field: 'companyName',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Business Type',
      field: 'businessType',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Full Name',
      field: 'fullName',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Title',
      field: 'title',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Campaign Name',
      field: 'campaignName',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Website',
      field: 'website',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Line of Business',
      field: 'lineOfBusiness',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'State',
      field: 'state',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'No. Of Employees',
      field: 'noOfEmployees',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Annual Revenue',
      field: 'annualRevenue',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Written Premium',
      field: 'writtenPremium',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Template Name',
      field: 'templateName',
      sortable: true,
      filter: true,
      flex: 1,
    },
  ];

  const columnDefsScheduled = isLeadPath ? [...baseColumns, ...leadColumns] : baseColumns;

  const baseSentColumns = [
    {
      headerName: 'To',
      field: 'toAddress',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Email Template Subject',
      field: 'subject',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return 'Unknown';
        return params.value.charAt(0).toUpperCase() + params.value.slice(1).toLowerCase();
      },
    },
    {
      headerName: 'Sent Date',
      field: 'createdDateTime',
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: filterParams,
      flex: 1,
      valueFormatter: DateFormatter,
    },
    {
      headerName: 'Sent By',
      field: 'fromAddress',
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: NameFormatter,
    },
  ];

  const leadSentColumns = [
    {
      headerName: 'Company Name',
      field: 'companyName',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Business Type',
      field: 'businessType',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Full Name',
      field: 'fullName',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Title',
      field: 'title',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Campaign Name',
      field: 'campaignName',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Website',
      field: 'website',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Line of Business',
      field: 'lineOfBusiness',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'State',
      field: 'state',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'No. Of Employees',
      field: 'noOfEmployees',
      sortable: true,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Annual Revenue',
      field: 'annualRevenue',
      sortable: true,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Written Premium',
      field: 'writtenPremium',
      sortable: true,
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
    {
      headerName: 'Template Name',
      field: 'templateName',
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
      },
      flex: 1,
    },
  ];

  const columnDefsSent = isLeadPath ? [...baseSentColumns, ...leadSentColumns] : baseSentColumns;

  const columnDefsStopped = [
    {
      headerName: 'To',
      field: 'toAddress',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Email Template Subject',
      field: 'subject',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Scheduled For Date',
      field: 'scheduleddate',
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: 'Stopped By',
      field: 'createdBy',
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: NameFormatter,
    },
  ];

  const gridOptionsSent = {
    // pagination: true,
    paginationPageSize: process.env.REACT_APP_DEFAULT_PAGESIZE,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressServerSideFullWidthLoadingRow: true,
    components: {
      customStatusBar: CustomStatusBar, // Ensure the correct reference
    },
    context: {
      moduleKey: keySent,
      navigate,
    },
    statusBar: {
      statusPanels: [
        {
          statusPanel: 'customStatusBar',
          align: 'center',
        },
      ],
    },
  };

  const gridOptionsSchedule = {
    paginationPageSize: process.env.REACT_APP_DEFAULT_PAGESIZE,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressServerSideFullWidthLoadingRow: true,
    components: {
      customStatusBar: CustomStatusBar, // Ensure the correct reference
    },
    context: {
      moduleKey: keySchedule,
      navigate,
    },
    statusBar: {
      statusPanels: [
        {
          statusPanel: 'customStatusBar',
          align: 'center',
        },
      ],
    },
  };

  const exportAll = async () => {
    setLoading(true);

    // Ensure filterJson is considered
    const fetchData = {
      ...contactInputData,
      pageSize: totalCountSent,
      module: isLeadPath ? 'Lead' : 'Contact',
      columnName: 'createdDateTime',
      status: activeTab === 'Scheduled' ? 'Scheduled' : '',
    };

    try {
      const response = await apiServices.exportEmail(fetchData);

      if (response?.data && response.status === 200) {
        setLoading(false);
        if (response?.data.includes('Export initiated.')) {
          toast.warning(response?.data); // Show toast
          setLoading(false);
        } else {
          // Convert data to Blob for better handling
          const blob = new Blob([response.data], {
            type: 'text/csv;charset=utf-8;',
          });
          const url = URL.createObjectURL(blob);

          // Create and trigger download
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', isLeadPath ? 'Lead Emails.csv' : 'Contact Emails.csv');
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          URL.revokeObjectURL(url); // Free memory
        }
      } else {
        console.error('No data received from API.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error exporting leads:', error);
      setLoading(false);
    }
  };

  const filterDefaultSetRef = useRef(null);

  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
      } else {
        let filterModel = event.api.getFilterModel();
        console.log('Original Filter Model:', filterModel);

        const updatedFilterModel = Object.keys(filterModel).reduce((acc, dynamicFilterKey) => {
          let filterData = filterModel[dynamicFilterKey];

          acc[dynamicFilterKey] = filterData;

          return acc;
        }, {});

        console.log('Updated Filter Model:', updatedFilterModel);

        const payload = {
          ...gridData?.inputPayload,
          filterJson: JSON.stringify(updatedFilterModel),
          pageNumber: 1, // Reset to first page on filter change
        };

        console.log('Filter Payload:', payload);

        dispatch(
          fetchDataAndUpdateStore({
            key: keySent,
            inputData: payload,
          })
        );
      }
    },
    [dispatch, totalEmailSent?.inputPayload]
  );
  const [gridParams, setGridParams] = useState(null);

  return (
    <>
      {loading && <Loader />}
      <div className="inner-page" style={containerStyle}>
        <div className="dash-container">
          <div className="tbl_container" id="massemailpage">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12">
                <div className="main-hd massEmailtabs">
                  <div>
                    <div className="slider-tabs">
                      <div className="tab-buttons">
                        {tabs.map((tab) => (
                          <button
                            key={tab}
                            className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="third-div">
                    {activeTab === 'Sent' && (
                      <div className="totalaction-count">
                        Total Emails Sent
                        <span className="bld-nmber">{totalCountSent}</span>
                      </div>
                    )}
                    {activeTab === 'Scheduled' && (
                      <div className="totalaction-count">
                        Total Schedule Emails{' '}
                        <span className="bld-nmber">{totalCountSchedule}</span>
                      </div>
                    )}
                    {activeTab === 'Sent' && (
                      <div className="heading-item">
                        <button className="add-btn" type="button" onClick={exportAll}>
                          Export
                        </button>
                      </div>
                    )}
                    {activeTab === 'Scheduled' && (
                      <div className="heading-item">
                        <button className="add-btn" type="button" onClick={exportAll}>
                          Export
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="tab-content massemail_container">
                  {activeTab === 'Scheduled' && (
                    <div>
                      <div
                        className="cutome_ag_theme"
                        style={{
                          width: '100%',
                          border: '1px solid #E5E5E5',
                          margin: '0 auto',
                        }}
                      >
                        <div
                          className={`ag-theme-${selectedTheme} tbl_height`}
                          style={{
                            height: 'calc(100vh - 173px)',
                            overflow: 'auto',
                          }}
                        >
                          <AgGridReact
                            ref={gridRef}
                            domLayout="fixedHeight"
                            columnDefs={columnDefsScheduled}
                            rowData={totalEmailSchedule}
                            defaultColDef={{ resizable: true }}
                            animateRows={true}
                            rowSelection="single"
                            gridOptions={gridOptionsSchedule}
                            columnTypes={columnTypes}
                            sideBar={sideBar}
                            onFilterChanged={handleFilterChanged}
                          />
                          {gridParams && (
                            <FilterHandler
                              params={gridParams}
                              gridData={totalEmailSchedule}
                              columnDefs={columnDefsScheduled}
                              filterDefaultSetRef={filterDefaultSetRef}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 'Sent' && (
                    <div>
                      {/* <div className="totalaction-count">Total Emails <span className="bld-nmber">{rowDataSent.length}</span></div> */}
                      <div
                        className="cutome_ag_theme"
                        style={{
                          width: '100%',
                          border: '1px solid #E5E5E5',
                          margin: '0 auto',
                        }}
                      >
                        <div
                          className={`ag-theme-${selectedTheme} tbl_height`}
                          style={{
                            height: 'calc(100vh - 173px)',
                            overflow: 'auto',
                          }}
                        >
                          <AgGridReact
                            ref={gridRef}
                            domLayout="fixedHeight"
                            columnDefs={columnDefsSent}
                            rowData={totalEmailSent}
                            defaultColDef={{ resizable: true }}
                            animateRows={true}
                            rowSelection="single"
                            gridOptions={gridOptionsSent}
                            columnTypes={columnTypes}
                            sideBar={sideBar}
                            onFilterChanged={handleFilterChanged}
                            onGridReady={(params) => setGridParams(params)}
                          />

                          {gridParams && (
                            <FilterHandler
                              params={gridParams}
                              gridData={totalEmailSent}
                              columnDefs={columnDefsSent}
                              filterDefaultSetRef={filterDefaultSetRef}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default MassEmailPage;
